import "./ProyectosCommon.css";
import { jsPDF } from "jspdf";
import { BasePageComponent } from "../../../shared/components/pages/BasePageComponent";
import {
  GastoResponse,
  ProyectoDetalleResponse,
  ProyectoItemDetalle,
  ProyectoResponse,
  PresupuestoResponse,
} from "../../../shared/response/proyectos/ProyectoResponse";
import { ProyectoService } from "../../services/proyectos/ProyectoService";
import { ProyectoDeleteService } from "../../services/proyectos/ProyectoDeleteService";
import { GastoService } from "../../services/proyectos/GastoService";
import { ActividadService } from "../../services/proyectos/ActividadService";
import { ActividadResponse } from "../../../shared/response/proyectos/ActividadResponse";
import { FaseResponse } from "../../../shared/response/proyectos/FaseResponse";
import { FaseService } from "../../services/proyectos/FaseService";
import { ProyectoWizard } from "./ProyectoWizard";
import { GastoModal } from "./GastoModal";
import { SolicitudModal } from "./SolicitudModal";
import { ConfirmDialogComponent } from "../../../shared/components/ConfirmDialogComponent";
import { errorMessageComponent } from "../../../shared/components/error/ErrorMessageComponent";
import { fmtCentavos, fmtPct, fmtFechaProy, fmtHace } from "../../../shared/utils/proyectosFormat";
import { formatKg } from "../../../shared/utils/formatUtils";
import { categoriaColor } from "../../../shared/utils/categoriaColors";
import { cultivoEmoji } from "../../../shared/utils/cultivoEmoji";
import { coincideBusqueda, palabrasBusqueda } from "../../../shared/utils/busquedaTexto";
import { downloadCsv } from "../../../shared/utils/csvUtils";

/**
 * Deep-link mínimo para abrir el detalle de un proyecto específico al navegar desde otra
 * página (p. ej. la tabla "recientes" del Dashboard): se fija el id justo antes de navegar
 * y `ProyectosPage` lo consume -y limpia- la primera vez que carga la lista.
 */
export const proyectoDeepLink = { pendingId: null as string | null, pendingMacroProyectoId: null as string | null };

const ESTADO_LABELS: Record<string, string> = {
  borrador: "Borrador", en_revision: "En revisión", aprobado: "Aprobado",
  activo: "Activo", cerrado: "Cerrado", suspendido: "Suspendido",
};

// Un paso a la vez: debe coincidir con TransicionesPermitidas en CambiarEstadoService.cs (backend).
const ESTADO_TRANSICIONES: Record<string, string[]> = {
  borrador: ["en_revision"],
  en_revision: ["aprobado", "borrador"],
  aprobado: ["activo", "borrador"],
  activo: ["cerrado", "suspendido"],
  suspendido: ["activo", "cerrado"],
  cerrado: [],
};

/** Avance real de la labor que marca el técnico — debe coincidir con Constantes.Proyectos.AvanceLabor. */
const AVANCE_LABEL: Record<string, string> = {
  pendiente: "Pendiente",
  en_ejecucion: "En ejecución",
  completado: "Completado",
};

/** Estado de una solicitud de cantidad (solo Nitrofert); lo calcula el servidor con lo entregado. */
const ESTADO_SOLICITUD_LABEL: Record<string, string> = {
  pendiente: "Pendiente",
  parcial: "Parcial",
  completa: "Completa",
};

interface EstadoVisual { label: string; cls: string }

function estadoVisual(p: ProyectoResponse): EstadoVisual {
  if (p.estado === "cerrado") return { label: "Cerrado", cls: "na" };
  if (p.estado === "borrador") return { label: "Borrador", cls: "borrador" };
  if (p.estado === "en_revision") return { label: "En revisión", cls: "am" };
  if (p.estado === "aprobado") return { label: "Aprobado", cls: "blue" };
  if (p.estado === "suspendido") return { label: "Suspendido", cls: "red" };
  // "activo": el estado en sí ya no aporta nada nuevo -lo interesante es cómo va la ejecución-.
  const base = p.capex + p.opexAnual;
  if (p.ejecutado === 0) return { label: "Sin avance", cls: "blue" };
  const desv = base > 0 ? (p.ejecutado - base) / base : 0;
  if (desv <= 0.1) return { label: "En ejecución", cls: "ok" };
  if (desv <= 0.2) return { label: "Alerta costo", cls: "am" };
  return { label: "Alerta costo alta", cls: "red" };
}

export class ProyectosPage extends BasePageComponent {
  private _all: ProyectoResponse[] = [];
  private _filtered: ProyectoResponse[] = [];
  private _detalle: ProyectoDetalleResponse | null = null;
  private _presupuesto: PresupuestoResponse | null = null;
  private _actividades: ActividadResponse[] = [];
  private _fases: FaseResponse[] = [];
  private _detalleTab: "presupuesto" | "fases" | "actividad" = "presupuesto";
  /** La tabla de presupuesto esconde los ítems apagados que no tienen gasto; esto los revela. */
  private _mostrarInactivos = false;
  /** Texto del buscador de la tabla de presupuesto (nombre, categoría o subsección). */
  private _itemBusqueda = "";
  private static readonly PAGE_SIZE = 24;
  private _page = 1;

  private _listaView!:   HTMLElement;
  private _detalleView!: HTMLElement;
  private _grid!:        HTMLElement;
  private _search!:      HTMLInputElement;
  private _fCultivo!:    HTMLSelectElement;
  private _estadoFiltro = "";
  private _fNucleo!:     HTMLSelectElement;
  private _fTecnico!:    HTMLSelectElement | null;
  private _fMacro!:      HTMLSelectElement | null;
  private _sub!:         HTMLElement;

  /**
   * @param _modoTecnico Portal del técnico ("Mis proyectos"): solo sus proyectos, sin editar el paquete.
   * @param _esNitrofert Plataforma de Nitrofert: los ítems no registran gastos, piden cantidades (`SolicitudModal`)
   *   que Nitrofert atiende por su API; los gastos se ven pero no se editan ni se eliminan.
   */
  constructor(private readonly _modoTecnico = false, private readonly _esNitrofert = false) { super(); }

  public getAppRoute(): string { return this._modoTecnico ? "/proyectos/mis-proyectos" : "/proyectos/proyectos"; }
  public getPageTitle(): string | null { return this._modoTecnico ? "Mis proyectos" : "Proyectos"; }

  protected override renderPageContent(pageContent: HTMLDivElement): void {
    pageContent.className = "sx-page pr-page";
    pageContent.innerHTML = `
      <div class="pr-content">
        <div class="pr-view" id="prv-lista">
          <div class="pr-pg-hd">
            <div>
              <div class="pr-pg-ey">Proyectos</div>
              <div class="pr-pg-ti">${this._modoTecnico ? "Mis proyectos" : "Proyectos"}</div>
              <div class="pr-pg-su" id="prv-sub">${this._modoTecnico ? "Proyectos asignados a ti" : "Proyectos plurianuales por cultivo"}</div>
            </div>
            <div style="display:flex;gap:8px">
              <button class="sx-btn sx-btn-secondary sx-btn-sm" id="prv-exportar"><i class="ph ph-download-simple"></i> Exportar</button>
              ${this._modoTecnico ? "" : `<button class="sx-btn sx-btn-primary sx-btn-sm" id="prv-nuevo"><i class="ph ph-plus"></i> Nuevo proyecto</button>`}
            </div>
          </div>
          <div class="pr-frow">
            <div class="pr-sw"><i class="ph ph-magnifying-glass"></i><input class="pr-si" id="prv-search" placeholder="Buscar por nombre, cliente o núcleo…"></div>
            <div class="pr-vsep"></div>
            <div class="pr-stabs" id="prv-estado-tabs">
              <button class="pr-stab on" data-st="">Todos <span class="pr-stab-cnt"></span></button>
              <button class="pr-stab" data-st="activo">Activos <span class="pr-stab-cnt"></span></button>
              <button class="pr-stab" data-st="en_revision">En revisión <span class="pr-stab-cnt"></span></button>
              <button class="pr-stab" data-st="aprobado">Aprobados <span class="pr-stab-cnt"></span></button>
              <button class="pr-stab" data-st="borrador">Borrador <span class="pr-stab-cnt"></span></button>
            </div>
            <div class="pr-vsep"></div>
            <span class="pr-flbl">Núcleo:</span>
            <select class="pr-fsel" id="prv-nucleo"><option value="">Todos</option></select>
            <span class="pr-flbl">Cultivo:</span>
            <select class="pr-fsel" id="prv-cultivo"><option value="">Todos</option></select>
            ${this._modoTecnico ? "" : `<span class="pr-flbl">Técnico:</span><select class="pr-fsel" id="prv-tecnico"><option value="">Todos</option></select>`}
            ${this._modoTecnico ? "" : `<span class="pr-flbl">Macro:</span><select class="pr-fsel" id="prv-macro"><option value="">Todos</option></select>`}
          </div>
          <div class="pr-tbl-wrap">
            <table class="pr-tbl">
              <thead>
                <tr><th>Proyecto</th><th>Cultivo</th><th>Núcleo</th><th>Técnico</th><th>Área</th><th>Monto estimado</th><th>Estado</th><th>Creado</th><th></th></tr>
              </thead>
              <tbody id="prv-grid"><tr><td colspan="9" class="pr-empty">Cargando…</td></tr></tbody>
            </table>
          </div>
          <div class="pr-pagination" id="prv-pagination"></div>
        </div>

        <div class="pr-view" id="prv-detalle" style="display:none"></div>
      </div>
    `;

    this._listaView   = document.getElementById("prv-lista")!;
    this._detalleView = document.getElementById("prv-detalle")!;
    this._grid        = document.getElementById("prv-grid")!;
    this._search      = document.getElementById("prv-search")! as HTMLInputElement;
    this._fCultivo    = document.getElementById("prv-cultivo")! as HTMLSelectElement;
    this._fNucleo     = document.getElementById("prv-nucleo")! as HTMLSelectElement;
    this._fTecnico    = document.getElementById("prv-tecnico") as HTMLSelectElement;
    this._fMacro      = document.getElementById("prv-macro") as HTMLSelectElement;
    this._sub         = document.getElementById("prv-sub")!;

    document.getElementById("prv-nuevo")?.addEventListener("click", () => this._openWizard());
    document.getElementById("prv-exportar")!.onclick = () => this._onExportar();
    this._search.oninput = () => this._filterAndRender();
    this._fCultivo.onchange = () => this._filterAndRender();
    document.querySelectorAll<HTMLButtonElement>("#prv-estado-tabs .pr-stab").forEach((btn) => {
      btn.onclick = () => {
        this._estadoFiltro = btn.dataset.st ?? "";
        document.querySelectorAll<HTMLButtonElement>("#prv-estado-tabs .pr-stab").forEach((b) => b.classList.toggle("on", b === btn));
        this._filterAndRender();
      };
    });
    this._fNucleo.onchange = () => this._filterAndRender();
    this._fTecnico?.addEventListener("change", () => this._filterAndRender());
    this._fMacro?.addEventListener("change", () => this._filterAndRender());

    void this._loadData();
  }

  protected override beforeDispose(): void {}

  // ── Data ───────────────────────────────────────────────────────────────────
  private async _loadData(): Promise<void> {
    try {
      const items = this._modoTecnico
        ? await ProyectoService.instance.getMisProyectosTecnico()
        : await ProyectoService.instance.get();
      this._all = items.filter((p) => !p.isEliminado)
        .sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
      this._populateCultivos();
      this._populateNucleosYTecnicos();

      // Deep-link desde otra página: filtrar por Macro-Proyecto (desde "Ver proyectos"
      // en Macro-Proyectos) o abrir directamente el detalle de un proyecto (desde la
      // tabla "recientes" del Dashboard). Se consume una sola vez.
      const pendingMacro = proyectoDeepLink.pendingMacroProyectoId;
      if (pendingMacro && this._fMacro) {
        proyectoDeepLink.pendingMacroProyectoId = null;
        this._fMacro.value = pendingMacro;
      }
      this._filterAndRender();

      const pendingId = proyectoDeepLink.pendingId;
      if (pendingId) {
        proyectoDeepLink.pendingId = null;
        if (this._all.some((p) => p.id === pendingId)) void this._openDetalle(pendingId);
      }
    } catch (error) {
      errorMessageComponent.addError("Error al cargar proyectos", (error as Error).message);
      this._grid.innerHTML = `<div class="pr-empty">Error al cargar</div>`;
    }
  }

  private _populateCultivos(): void {
    const cultivos = [...new Set(this._all.map((p) => p.cultivoNombre).filter(Boolean))].sort();
    this._fCultivo.innerHTML = `<option value="">Todos los cultivos</option>` +
      cultivos.map((c) => `<option value="${this._esc(c)}">${this._esc(c)}</option>`).join("");
  }

  private _populateNucleosYTecnicos(): void {
    const prevNucleo = this._fNucleo.value;
    const nucleos = [...new Map(this._all.filter((p) => p.nucleoId).map((p) => [p.nucleoId as string, p.nucleoNombre as string])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1]));
    this._fNucleo.innerHTML = `<option value="">Todos los núcleos</option>` +
      nucleos.map(([id, nombre]) => `<option value="${id}">${this._esc(nombre)}</option>`).join("");
    this._fNucleo.value = prevNucleo;

    if (!this._fTecnico) return;
    const prevTecnico = this._fTecnico.value;
    const tecnicos = [...new Map(this._all.map((p) => [p.tecnicoId, p.tecnicoNombre])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1]));
    this._fTecnico.innerHTML = `<option value="">Todos los técnicos</option>` +
      tecnicos.map(([id, nombre]) => `<option value="${id}">${this._esc(nombre)}</option>`).join("");
    this._fTecnico.value = prevTecnico;

    if (!this._fMacro) return;
    const prevMacro = this._fMacro.value;
    const macros = [...new Map(this._all.filter((p) => p.macroProyectoId).map((p) => [p.macroProyectoId as string, p.macroProyectoNombre as string])).entries()]
      .sort((a, b) => a[1].localeCompare(b[1]));
    this._fMacro.innerHTML = `<option value="">Todos los macro-proyectos</option>` +
      macros.map(([id, nombre]) => `<option value="${id}">${this._esc(nombre)}</option>`).join("");
    this._fMacro.value = prevMacro;
  }

  /**
   * Filtra los proyectos por lo escrito (nombre, cliente o núcleo, con la regla común de `busquedaTexto`: todas las
   * palabras, sin distinguir mayúsculas ni tildes) y por cultivo, estado, núcleo, técnico y macro-proyecto, y repinta.
   */
  private _filterAndRender(): void {
    const palabras = palabrasBusqueda(this._search.value);
    const fc = this._fCultivo.value;
    const fe = this._estadoFiltro;
    const fn = this._fNucleo.value;
    const ft = this._fTecnico?.value ?? "";
    const fm = this._fMacro?.value ?? "";
    this._filtered = this._all.filter((p) => {
      const matchQ = coincideBusqueda(palabras, p.nombre, p.clienteNombre, p.nucleoNombre);
      const matchC = !fc || p.cultivoNombre === fc;
      const matchE = !fe || p.estado === fe;
      const matchN = !fn || p.nucleoId === fn;
      const matchT = !ft || p.tecnicoId === ft;
      const matchM = !fm || p.macroProyectoId === fm;
      return matchQ && matchC && matchE && matchN && matchT && matchM;
    });
    const activos = this._all.filter((p) => p.estado === "activo").length;
    this._sub.textContent = `${activos} proyecto${activos === 1 ? "" : "s"} activo${activos === 1 ? "" : "s"} · ${this._all.length} en total`;
    this._updateEstadoTabCounts();
    this._page = 1;
    this._renderGrid();
  }

  private _updateEstadoTabCounts(): void {
    const counts: Record<string, number> = {};
    for (const p of this._all) counts[p.estado] = (counts[p.estado] ?? 0) + 1;
    document.querySelectorAll<HTMLButtonElement>("#prv-estado-tabs .pr-stab").forEach((btn) => {
      const st = btn.dataset.st ?? "";
      const n = st ? (counts[st] ?? 0) : this._all.length;
      btn.querySelector(".pr-stab-cnt")!.textContent = String(n);
    });
  }

  private _renderGrid(): void {
    const list = this._filtered;
    if (list.length === 0) {
      this._grid.innerHTML = `<tr><td colspan="9" class="pr-empty">Sin proyectos. Crea el primero con "Nuevo proyecto".</td></tr>`;
      document.getElementById("prv-pagination")!.innerHTML = "";
      return;
    }
    const totalPages = Math.max(1, Math.ceil(list.length / ProyectosPage.PAGE_SIZE));
    this._page = Math.min(Math.max(1, this._page), totalPages);
    const start = (this._page - 1) * ProyectosPage.PAGE_SIZE;
    const pageItems = list.slice(start, start + ProyectosPage.PAGE_SIZE);

    this._grid.innerHTML = pageItems.map((p) => {
      const ev = estadoVisual(p);
      const total = p.capex + p.opexAnual;
      return `
        <tr data-id="${p.id}" style="cursor:pointer">
          <td>
            <div style="font-weight:var(--sx-weight-semibold)">${this._esc(p.nombre)}</div>
            <div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">${this._esc(p.clienteNombre || "—")}${p.municipio ? " · " + this._esc(p.municipio) : ""}</div>
          </td>
          <td><span class="pr-cultivo-emoji" aria-hidden="true">${cultivoEmoji(p.cultivoNombre)}</span>${this._esc(p.cultivoNombre)}${p.variedad ? ` <span style="color:var(--sx-text-tertiary)">${this._esc(p.variedad)}</span>` : ""}</td>
          <td>${p.nucleoNombre ? this._esc(p.nucleoNombre) : "—"}</td>
          <td><span class="pr-avatar">${this._iniciales(p.tecnicoNombre)}</span>${this._esc(p.tecnicoNombre)}</td>
          <td class="pr-num">${formatKg(p.ha)} Ha</td>
          <td class="pr-num">
            <div style="font-weight:var(--sx-weight-semibold)">${fmtCentavos(total)}</div>
            <div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">CAPEX ${fmtCentavos(p.capex)} · OPEX ${fmtCentavos(p.opexAnual)}</div>
          </td>
          <td><span class="pr-pill ${ev.cls}">${ev.label}</span></td>
          <td style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary)">${p.createdAt ? fmtFechaProy(p.createdAt) : "—"}</td>
          <td>
            <div class="pr-actions pr-actions-hover">
              <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-view="${p.id}" title="Ver proyecto"><i class="ph ph-eye"></i></button>
            </div>
          </td>
        </tr>`;
    }).join("");

    this._grid.querySelectorAll<HTMLElement>("tr[data-id]").forEach((row) => {
      row.onclick = () => void this._openDetalle(row.dataset.id!);
    });

    this._renderPagination(totalPages, list.length, start, pageItems.length);
  }

  private _renderPagination(totalPages: number, totalItems: number, start: number, pageCount: number): void {
    const el = document.getElementById("prv-pagination")!;
    if (totalPages <= 1) { el.innerHTML = ""; return; }

    const pages: (number | "…")[] = [];
    const cur = this._page;
    for (let i = 1; i <= totalPages; i++) {
      if (i === 1 || i === totalPages || Math.abs(i - cur) <= 1) pages.push(i);
      else if (pages[pages.length - 1] !== "…") pages.push("…");
    }

    el.innerHTML = `
      <span class="pr-pg-info">${start + 1}–${start + pageCount} de ${totalItems}</span>
      <div class="pr-pg-btns">
        <button class="pr-pg-btn" data-pg="${cur - 1}" ${cur === 1 ? "disabled" : ""}><i class="ph ph-caret-left"></i></button>
        ${pages.map((p) => p === "…"
          ? `<span class="pr-pg-ellipsis">…</span>`
          : `<button class="pr-pg-btn ${p === cur ? "on" : ""}" data-pg="${p}">${p}</button>`).join("")}
        <button class="pr-pg-btn" data-pg="${cur + 1}" ${cur === totalPages ? "disabled" : ""}><i class="ph ph-caret-right"></i></button>
      </div>`;

    el.querySelectorAll<HTMLButtonElement>(".pr-pg-btn[data-pg]").forEach((btn) => {
      btn.onclick = () => this._setPage(Number(btn.dataset.pg));
    });
  }

  private _setPage(page: number): void {
    if (page === this._page) return;
    this._page = page;
    this._renderGrid();
    this._grid.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  private _onExportar(): void {
    const rows: (string | number)[][] = [
      ["Proyecto", "Cliente", "Cultivo", "Variedad", "Núcleo", "Técnico", "Área (Ha)", "CAPEX", "OPEX", "Total estimado", "Ejecutado", "Estado", "Creado"],
      ...this._filtered.map((p) => [
        p.nombre, p.clienteNombre || "", p.cultivoNombre, p.variedad || "", p.nucleoNombre || "", p.tecnicoNombre,
        p.ha, p.capex, p.opexAnual, p.capex + p.opexAnual, p.ejecutado, ESTADO_LABELS[p.estado] ?? p.estado,
        p.createdAt ? fmtFechaProy(p.createdAt) : "",
      ]),
    ];
    downloadCsv(`proyectos_${new Date().toISOString().slice(0, 10)}.csv`, rows);
  }

  // ── Wizard ─────────────────────────────────────────────────────────────────
  private _openWizard(): void {
    new ProyectoWizard((created) => {
      this._all = [created, ...this._all];
      this._populateCultivos();
      this._populateNucleosYTecnicos();
      this._filterAndRender();
      void this._openDetalle(created.id);
    }).init();
  }

  /** Reabre el wizard Paso 2 con los ítems actuales del proyecto (corregir precios/cantidades). */
  private _onEditPaquete(): void {
    if (!this._detalle) return;
    new ProyectoWizard(
      () => void this._reloadDetalle(),
      { proyecto: this._detalle.proyecto, items: this._detalle.items },
    ).init();
  }

  // ── Detalle (read-only en 3a; gastos/presupuesto/PDF en 3b) ─────────────────
  private async _openDetalle(id: string): Promise<void> {
    this._listaView.style.display = "none";
    this._detalleView.style.display = "block";
    this._detalleView.innerHTML = `<div class="pr-empty">Cargando detalle…</div>`;
    this._detalleTab = "presupuesto";
    try {
      const [detalle, presupuesto, actividades, fases] = await Promise.all([
        ProyectoService.instance.getDetalle(id),
        ProyectoService.instance.getPresupuesto(id).catch(() => null),
        ActividadService.instance.getByProyectoId(id).catch(() => [] as ActividadResponse[]),
        this._fases.length > 0 ? Promise.resolve(this._fases) : FaseService.instance.get().catch(() => [] as FaseResponse[]),
      ]);
      this._detalle = detalle;
      this._presupuesto = presupuesto;
      this._fases = fases.filter((f) => !f.isEliminado).sort((a, b) => a.orden - b.orden);
      this._actividades = actividades;
      this._renderDetalle();
    } catch (error) {
      errorMessageComponent.addError("Error al cargar el detalle", (error as Error).message);
      this._backToLista();
    }
  }

  private async _reloadDetalle(): Promise<void> {
    if (!this._detalle) return;
    const id = this._detalle.proyecto.id;
    try {
      const [detalle, presupuesto, actividades] = await Promise.all([
        ProyectoService.instance.getDetalle(id),
        ProyectoService.instance.getPresupuesto(id).catch(() => this._presupuesto),
        ActividadService.instance.getByProyectoId(id).catch(() => this._actividades),
      ]);
      this._detalle = detalle;
      this._presupuesto = presupuesto;
      this._actividades = actividades;
      this._all = this._all.map((x) => (x.id === detalle.proyecto.id ? detalle.proyecto : x));
      this._renderDetalle();
    } catch (error) {
      errorMessageComponent.addError("Error al recargar", (error as Error).message);
    }
  }

  private _backToLista(): void {
    this._detalleView.style.display = "none";
    this._listaView.style.display = "block";
    this._detalle = null;
    this._filterAndRender();
  }

  private _renderDetalle(): void {
    const d = this._detalle;
    if (!d) return;
    const p = d.proyecto;
    const k = d.kpis;
    const semColor = k.semaforo === "rojo" ? "var(--sx-error)" : k.semaforo === "amarillo" ? "var(--sx-warning)" : "var(--sx-success)";
    const avancePres = k.presupuestoTotal > 0 ? Math.round((k.ejecutadoTotal / k.presupuestoTotal) * 100) : 0;

    this._detalleView.innerHTML = `
      <div class="pr-det-hd">
        <button class="sx-btn sx-btn-ghost sx-btn-sm" id="prd-back"><i class="ph ph-arrow-left"></i> Proyectos</button>
        <span class="pr-det-avatar" aria-hidden="true">${cultivoEmoji(p.cultivoNombre)}</span>
        <span class="pr-det-name">${this._esc(p.nombre)}</span>
        <span class="pr-pill ${estadoVisual(p).cls}">${estadoVisual(p).label}</span>
        <div style="flex:1"></div>
        ${this._modoTecnico ? "" : `<button class="sx-btn sx-btn-secondary sx-btn-sm" id="prd-edit-paquete"><i class="ph ph-list-checks"></i> Editar paquete</button>`}
        ${(ESTADO_TRANSICIONES[p.estado] ?? []).length === 0 ? "" : `<select class="pr-fsel" id="prd-estado">
          <option value="${p.estado}" selected>${ESTADO_LABELS[p.estado]}</option>
          ${ESTADO_TRANSICIONES[p.estado].map((e) => `<option value="${e}">${ESTADO_LABELS[e]}</option>`).join("")}
        </select>`}
        <button class="sx-btn sx-btn-secondary sx-btn-sm" id="prd-pdf"><i class="ph ph-file-pdf"></i> Exportar PDF</button>
        ${this._modoTecnico ? "" : `<button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" id="prd-del" title="Eliminar"><i class="ph ph-trash"></i></button>`}
      </div>

      <div class="pr-det-sub">
        ${this._esc(p.clienteNombre)} · ${this._esc(p.cultivoNombre)}${p.variedad ? " " + this._esc(p.variedad) : ""} · ${formatKg(p.ha)} Ha
        ${p.municipio ? " · " + this._esc(p.municipio) : ""} · Técnico ${this._esc(p.tecnicoNombre)}
        · Snapshot ${p.snapshotAño} (IPC ${fmtPct(p.snapshotIpc)})
        ${p.createdAt ? ` · Creado ${fmtHace(p.createdAt)}` : ""}
      </div>

      <div class="pr-kstrip" style="grid-template-columns:repeat(6,1fr)">
        ${this._kpi(fmtCentavos(k.presupuestoTotal), "PRESUPUESTO TOTAL", "pr-k-val-money", `CAPEX ${fmtCentavos(p.capex)} · OPEX ${fmtCentavos(p.opexAnual)}`)}
        ${this._kpi(fmtCentavos(k.ejecutadoTotal), "EJECUTADO", "green pr-k-val-money", `${avancePres}% del presupuesto`)}
        ${this._kpi(fmtCentavos(k.disponible), "DISPONIBLE", "pr-k-val-money", "")}
        ${this._kpi((k.desviacionGlobal >= 0 ? "+" : "") + fmtPct(k.desviacionGlobal), "DESVIACIÓN GLOBAL", k.desviacionGlobal > 0.1 ? "red" : "", "")}
        ${this._kpi(`${p.itemsActivos} / ${p.totalItems}`, "ÍTEMS ACTIVOS", "", p.itemsSinPrecio > 0 ? `${p.itemsSinPrecio} sin precio` : "habilitados")}
        <div class="pr-kcard"><div class="pr-k-val" style="color:${semColor};font-size:16px">${k.semaforo.toUpperCase()}</div><div class="pr-k-lbl">SEMÁFORO</div><div class="pr-k-sub">${k.itemsEnAlerta} ítem${k.itemsEnAlerta === 1 ? "" : "s"} en alerta</div></div>
      </div>

      <div class="pr-tabs">
        <button class="pr-tab ${this._detalleTab === "presupuesto" ? "active" : ""}" data-dtab="presupuesto">Presupuesto</button>
        <button class="pr-tab ${this._detalleTab === "fases" ? "active" : ""}" data-dtab="fases">Fases</button>
        <button class="pr-tab ${this._detalleTab === "actividad" ? "active" : ""}" data-dtab="actividad">Actividad</button>
      </div>

      <div id="prd-tab-presupuesto" ${this._detalleTab === "presupuesto" ? "" : "hidden"}>
        ${this._presupuestoPanelHtml()}
        <div id="prd-items"></div>
      </div>
      <div id="prd-tab-fases" ${this._detalleTab === "fases" ? "" : "hidden"}>
        ${this._fasesPanelHtml()}
      </div>
      <div id="prd-tab-actividad" ${this._detalleTab === "actividad" ? "" : "hidden"}>
        ${this._actividadPanelHtml()}
      </div>
    `;

    this._renderItems(d.items);

    document.getElementById("prd-back")!.onclick = () => this._backToLista();
    document.getElementById("prd-pdf")!.onclick = () => this._exportPdf();
    document.getElementById("prd-edit-paquete")?.addEventListener("click", () => this._onEditPaquete());
    document.getElementById("prd-del")?.addEventListener("click", () => this._onDelete(p));
    const estadoSel = document.getElementById("prd-estado") as HTMLSelectElement | null;
    if (estadoSel) estadoSel.onchange = (e) => void this._onEstado((e.target as HTMLSelectElement).value);
    document.getElementById("prd-act-add")?.addEventListener("click", () => void this._onAgregarComentario());
    this._detalleView.querySelectorAll<HTMLButtonElement>(".pr-tab").forEach((btn) => {
      btn.onclick = () => this._setDetalleTab(btn.dataset.dtab as typeof this._detalleTab);
    });
  }

  private _setDetalleTab(tab: "presupuesto" | "fases" | "actividad"): void {
    if (tab === this._detalleTab) return;
    this._detalleTab = tab;
    this._detalleView.querySelectorAll<HTMLButtonElement>(".pr-tab").forEach((btn) => {
      btn.classList.toggle("active", btn.dataset.dtab === tab);
    });
    (["presupuesto", "fases", "actividad"] as const).forEach((t) => {
      document.getElementById(`prd-tab-${t}`)!.hidden = t !== tab;
    });
  }

  private static readonly ACT_ICONS: Record<string, string> = {
    creado: "ph-plus-circle", editado: "ph-pencil-simple", estado: "ph-arrows-clockwise", comentario: "ph-chat-text",
    gasto: "ph-currency-circle-dollar",
  };

  private _actividadPanelHtml(): string {
    const items = [...this._actividades].sort((a, b) => Number(b.createdAt) - Number(a.createdAt));
    return `
      <div class="pr-panel" style="margin-top:16px">
        <div class="pr-panel-t">Actividad</div>
        <div style="display:flex;gap:8px;margin-bottom:12px">
          <input class="sx-input" id="prd-act-input" type="text" maxlength="200" placeholder="Agregar un comentario…">
          <button class="sx-btn sx-btn-secondary sx-btn-sm" id="prd-act-add">Agregar</button>
        </div>
        ${items.length === 0 ? `<div class="pr-empty">Sin actividad registrada todavía.</div>` : items.map((a) => `
          <div class="pr-act-item">
            <div class="pr-act-dot pr-act-dot-${this._esc(a.tipo)}"><i class="ph ${ProyectosPage.ACT_ICONS[a.tipo] ?? "ph-chat-text"}"></i></div>
            <div class="pr-act-body">
              <div class="pr-act-title">${this._esc(a.titulo)}</div>
              ${a.descripcion ? `<div class="pr-act-desc">${a.usuarioNombre ? `<span class="pr-act-user">${this._esc(a.usuarioNombre)}</span> ` : ""}${this._esc(a.descripcion)}</div>` : (a.usuarioNombre ? `<div class="pr-act-desc"><span class="pr-act-user">${this._esc(a.usuarioNombre)}</span></div>` : "")}
              <div class="pr-act-time">${fmtFechaProy(a.createdAt)}</div>
            </div>
          </div>`).join("")}
      </div>`;
  }

  private async _onAgregarComentario(): Promise<void> {
    if (!this._detalle) return;
    const input = document.getElementById("prd-act-input") as HTMLInputElement;
    const titulo = input.value.trim();
    if (!titulo) return;
    const btn = document.getElementById("prd-act-add") as HTMLButtonElement;
    btn.disabled = true;
    try {
      const saved = await ActividadService.instance.agregarComentario(this._detalle.proyecto.id, { titulo, descripcion: null });
      this._actividades = [saved, ...this._actividades];
      input.value = "";
      this._renderDetalle();
    } catch (error) {
      errorMessageComponent.addError("Error al agregar el comentario", (error as Error).message);
    } finally {
      btn.disabled = false;
    }
  }

  private _presupuestoPanelHtml(): string {
    const b = this._presupuesto;
    if (!b) return "";
    return `
      ${this._limiteAdvisoryHtml()}
      <div class="pr-panel" style="margin-bottom:16px">
        <div class="pr-panel-t">Presupuesto proyectado hasta cosecha (${b.mesesACosecha} meses, OPEX con IPC compuesto)</div>
        <div class="pr-kstrip" style="grid-template-columns:repeat(3,1fr);margin-bottom:${b.presupuestoCompleto ? "0" : "12px"}">
          ${this._kpi(fmtCentavos(b.capex), "CAPEX", "", "")}
          ${this._kpi(fmtCentavos(b.opexProyectado), "OPEX PROYECTADO", "", "")}
          ${this._kpi(fmtCentavos(b.total), "TOTAL", "", "")}
        </div>
        ${b.presupuestoCompleto ? "" : `<div class="pr-empty" style="color:var(--sx-warning)">${b.itemsSinPrecio.length} ítem${b.itemsSinPrecio.length === 1 ? "" : "s"} activo${b.itemsSinPrecio.length === 1 ? "" : "s"} sin precio definido — el proyectado no los incluye.</div>`}
        ${this._detalleAnualHtml(b)}
      </div>`;
  }

  /**
   * Desglose año a año de la proyección (`detalleAnual`): una barra apilada por año, CAPEX en azul
   * (todo en el año 1) y OPEX en ámbar, mismos colores que los badges de fase CAPEX/OPEX.
   *
   * Con un solo año no se dibuja: la serie diría exactamente lo mismo que los tres KPIs de arriba
   * (total = capex + opex de ese único año), así que sería ruido.
   */
  private _detalleAnualHtml(b: PresupuestoResponse): string {
    const años = b.detalleAnual ?? [];
    if (años.length < 2) return "";

    // Escala por el año más caro, no por el total: si no, el año 1 (que carga todo el CAPEX) deja
    // los demás como una rayita invisible.
    const maxAño = Math.max(...años.map((a) => a.total), 1);
    let acumulado = 0;

    return `
      <div class="pr-anual" style="margin-top:16px;padding-top:14px;border-top:1px solid var(--sx-border-light)">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px">
          <div class="pr-panel-t" style="margin:0">Desembolso año a año</div>
          <div style="display:flex;gap:12px;font-size:var(--sx-text-xs);color:var(--sx-text-secondary)">
            <span><span class="pr-dot" style="background:var(--sx-info);display:inline-block;margin-right:4px"></span>CAPEX</span>
            <span><span class="pr-dot" style="background:var(--sx-warning);display:inline-block;margin-right:4px"></span>OPEX</span>
          </div>
        </div>
        ${años.map((a) => {
          acumulado += a.total;
          // Los dos segmentos se miden contra el MISMO maxAño para que la barra apilada sea
          // proporcional al desembolso real del año.
          const wCapex = (a.capex / maxAño) * 100;
          const wOpex = (a.opex / maxAño) * 100;
          const parcial = a.fraccion < 1 ? ` · ${Math.round(a.fraccion * 12)} meses` : "";
          return `
            <div class="pr-bar-row pr-anual-row" data-anio="${a.anio}">
              <span class="pr-bar-lbl">Año ${a.anio}${parcial}</span>
              <div class="pr-bar-track pr-bar-track--stack">
                ${a.capex > 0 ? `<div class="pr-bar-fill" style="width:${wCapex}%;background:var(--sx-info)" title="CAPEX ${fmtCentavos(a.capex)}"></div>` : ""}
                ${a.opex > 0 ? `<div class="pr-bar-fill" style="width:${wOpex}%;background:var(--sx-warning)" title="OPEX ${fmtCentavos(a.opex)}"></div>` : ""}
              </div>
              <span class="pr-bar-val pr-anual-total" style="width:104px" title="Acumulado ${fmtCentavos(acumulado)}">${fmtCentavos(a.total)}</span>
            </div>`;
        }).join("")}
        <div style="font-size:var(--sx-text-xs);color:var(--sx-text-tertiary);margin-top:4px">
          El OPEX de cada año ya viene capitalizado con el IPC de su categoría; el CAPEX se imputa al año 1.
        </div>
      </div>`;
  }

  // Compara el presupuesto actual (Capex + OpexAnual, igual criterio que el wizard) contra el
  // límite financiero de referencia que se fijó al crear el proyecto -si tiene uno-.
  private _limiteAdvisoryHtml(): string {
    const p = this._detalle?.proyecto;
    const limite = p?.limiteFinancieroReferencial;
    if (!p || limite == null) return "";
    const total = this._detalle!.kpis.presupuestoTotal;
    const superaLimite = total > limite;
    return `
      <div class="pr-alert-warn" style="margin-bottom:14px;${superaLimite ? "" : "background:var(--sx-success-bg);border-color:var(--sx-success-border);color:var(--sx-success)"}">
        <strong>${superaLimite ? "Presupuesto sobre el límite financiero de referencia" : "Presupuesto dentro del límite financiero de referencia"}${p.entidadFinancieraReferencia ? ` (${this._esc(p.entidadFinancieraReferencia)})` : ""}</strong>
        <div style="margin-top:4px">
          Monto estimado <strong>${fmtCentavos(total)}</strong> · Límite ${fmtCentavos(limite)}
          ${superaLimite ? ` · Diferencia <strong>+${fmtCentavos(total - limite)}</strong>` : ""}
        </div>
        <div style="margin-top:4px;font-size:var(--sx-text-xs)">Esto es informativo — el sistema no bloquea la creación.</div>
      </div>`;
  }

  private static readonly FASE_MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  private _faseFechaRango(p: ProyectoResponse, fase: FaseResponse): string {
    const dur = fase.mesFin - fase.mesInicio;
    if (!p.fechaInicio) return `Mes ${fase.mesInicio}–${fase.mesFin} · ${dur} mes${dur === 1 ? "" : "es"}`;
    const inicio = new Date(p.fechaInicio);
    const d1 = new Date(inicio.getFullYear(), inicio.getMonth() + fase.mesInicio, 1);
    const d2 = new Date(inicio.getFullYear(), inicio.getMonth() + fase.mesFin, 1);
    return `${ProyectosPage.FASE_MESES[d1.getMonth()]} ${d1.getFullYear()} – ${ProyectosPage.FASE_MESES[d2.getMonth()]} ${d2.getFullYear()} · ${dur} mes${dur === 1 ? "" : "es"}`;
  }

  private _fasesPanelHtml(): string {
    const d = this._detalle;
    if (!d) return "";
    const p = d.proyecto;
    const items = d.items.filter((it) => it.activo);
    const groups: { fase: FaseResponse | null; items: ProyectoItemDetalle[] }[] = this._fases
      .map((fase) => ({ fase, items: items.filter((it) => it.faseCodigo === fase.codigo) }))
      .filter((g) => g.items.length > 0);
    const sinFase = items.filter((it) => !it.faseCodigo);
    if (sinFase.length > 0) groups.push({ fase: null, items: sinFase });

    if (groups.length === 0) return `<div class="pr-empty" style="margin-top:16px">Este proyecto no tiene ítems configurados.</div>`;

    return `<div style="margin-top:16px;display:flex;flex-direction:column;gap:16px">
      ${groups.map((g) => this._faseCardHtml(p, g)).join("")}
    </div>`;
  }

  private _faseCardHtml(p: ProyectoResponse, g: { fase: FaseResponse | null; items: ProyectoItemDetalle[] }): string {
    const { fase, items } = g;
    const presupuestado = items.reduce((s, it) => s + (it.presupuestado ?? 0), 0);
    const ejecutado = items.reduce((s, it) => s + it.ejecutado, 0);
    const avance = presupuestado > 0 ? Math.min(100, Math.round((ejecutado / presupuestado) * 100)) : 0;
    const estadoLabel = ejecutado === 0 ? "Sin iniciar" : avance >= 100 ? "Completada" : "En progreso";
    const tipo = fase?.tipo ?? (items.every((it) => it.tipo === "OPEX") ? "OPEX" : "CAPEX");
    const badgeClass = tipo === "CAPEX" ? "fase-badge-capex" : "fase-badge-opex";
    const fillClass = tipo === "CAPEX" ? "fase-fill-capex" : "fase-fill-opex";
    const titulo = fase ? `${this._esc(fase.codigo)} — ${this._esc(fase.nombre)}` : "Sin fase asignada";
    const rango = fase ? this._faseFechaRango(p, fase) : `${items.length} ítem${items.length === 1 ? "" : "s"} sin fase asignada en el catálogo`;

    const subMap = new Map<string, ProyectoItemDetalle[]>();
    for (const it of items) {
      const key = it.sub || "General";
      if (!subMap.has(key)) subMap.set(key, []);
      subMap.get(key)!.push(it);
    }
    const subs = [...subMap.entries()].sort((a, b) => a[0].localeCompare(b[0]));

    return `
      <div class="fase-card">
        <div class="fase-header">
          <div><div class="fase-title">${titulo}</div><div class="fase-sub-range">${rango}</div></div>
          <span class="${badgeClass}">${tipo} · ${fmtCentavos(presupuestado)}</span>
        </div>
        <div class="fase-progress-bar"><div class="fase-progress-fill ${fillClass}" style="width:${avance}%"></div></div>
        <div class="fase-meta"><span>${estadoLabel}</span><span>${fmtCentavos(ejecutado)} / ${fmtCentavos(presupuestado)} ejecutado</span></div>
        <div class="fase-subs">
          ${subs.map(([sub, subItems]) => this._faseSubItemHtml(sub, subItems)).join("")}
        </div>
      </div>`;
  }

  private _faseSubItemHtml(sub: string, items: ProyectoItemDetalle[]): string {
    const presupuestado = items.reduce((s, it) => s + (it.presupuestado ?? 0), 0);
    const ejecutado = items.reduce((s, it) => s + it.ejecutado, 0);
    const avance = presupuestado > 0 ? Math.min(100, Math.round((ejecutado / presupuestado) * 100)) : 0;
    const color = avance >= 100 ? "var(--sx-success)" : avance > 0 ? "var(--sx-info)" : "var(--sx-border-strong)";
    return `
      <div class="fase-sub-item">
        <div class="fsi-name">${this._esc(sub)}</div>
        <div class="fsi-bar"><div class="fsi-fill" style="width:${avance}%;background:${color}"></div></div>
        <div class="fsi-meta"><span>${avance}%</span><span>${fmtCentavos(presupuestado)}</span></div>
      </div>`;
  }

  private _renderItems(items: ProyectoItemDetalle[]): void {
    const cont = document.getElementById("prd-items")!;
    items = items ?? [];
    if (items.length === 0) { cont.innerHTML = `<div class="pr-empty">Este proyecto no tiene ítems configurados.</div>`; return; }
    const soloLectura = this._modoTecnico;
    const nCols = soloLectura ? 8 : 9;
    const emptyTailCols = nCols - 4; // tras el rótulo (2) + Presupuestado + Ejecutado del subtotal/total.

    // Un ítem apagado solo es ruido si además no tiene gasto. Si lo tiene, su plata sigue contando en
    // el KPI "Ejecutado" y en los subtotales (Querys/Proyectos.cs suma los gastos de TODOS los ítems,
    // activos o no), así que esconderlo dejaría dinero visible en los totales e imposible de rastrear
    // en la tabla. Por eso esos se muestran siempre, aunque el interruptor esté apagado.
    const esRuido = (it: ProyectoItemDetalle): boolean => !it.activo && it.ejecutado === 0;
    const visible = (it: ProyectoItemDetalle): boolean => this._mostrarInactivos || !esRuido(it);
    const ocultos = items.filter(esRuido).length;

    const rows: string[] = [];
    let grandPresupuestado = 0;
    let grandEjecutado = 0;

    for (const tipo of ["CAPEX", "OPEX"]) {
      const delTipo = items.filter((it) => it.tipo === tipo);
      if (delTipo.length === 0) continue;
      const subs = [...new Set(delTipo.map((it) => it.sub))];
      rows.push(`<tr class="pr-budget-phase pr-budget-phase-${tipo.toLowerCase()}" data-tipo-key="${tipo}"><td colspan="${nCols}">${tipo}</td></tr>`);

      let tipoPresupuestado = 0;
      let tipoEjecutado = 0;
      for (const sub of subs) {
        const delSub = delTipo.filter((it) => it.sub === sub);
        if (delSub.some(visible)) {
          rows.push(`<tr class="pr-budget-sub" data-sub-key="${this._esc(sub)}"><td colspan="${nCols}">${this._esc(sub)}</td></tr>`);
        }
        delSub.forEach((it) => {
          // Presupuestado solo suma ítems activos -igual que Proyecto.Capex/OpexAnual en el backend-;
          // Ejecutado suma todos, activos o no, porque un gasto ya realizado no deja de haber pasado.
          if (it.activo) tipoPresupuestado += it.presupuestado ?? 0;
          tipoEjecutado += it.ejecutado;
          // El salto va DESPUÉS de sumar: ocultar una fila nunca debe mover un subtotal.
          if (!visible(it)) return;
          const desvCls = it.desviacion > 0.2 ? "var(--sx-error)" : it.desviacion > 0.1 ? "var(--sx-warning)" : "var(--sx-success)";
          const av = Math.round((it.avance || 0) * 100);
          // En Nitrofert los gastos los registra Nitrofert por su API: se ven, pero sin editar ni eliminar.
          const gastosHtml = (it.gastos ?? []).filter((g) => !g.isEliminado).map((g) => `
            <tr class="pr-gasto-row" data-de-item="${it.itemId}">
              <td colspan="3" style="padding-left:22px;color:var(--sx-text-tertiary)">↳ ${fmtFechaProy(g.fecha)} · ${formatKg(g.cantidadReal)} × ${fmtCentavos(g.precioUnitarioReal)}${g.proveedor ? " · " + this._esc(g.proveedor) : ""}${g.referencia ? " · Ref. " + this._esc(g.referencia) : ""}</td>
              <td class="pr-num">${fmtCentavos(g.total)}</td>
              <td colspan="${soloLectura ? 2 : 3}"></td>
              <td>${this._esNitrofert ? "" : `
                <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-gasto-edit="${g.id}" data-item-id="${it.itemId}" data-nombre="${this._esc(it.nombre)}" title="Editar gasto"><i class="ph ph-pencil"></i></button>
                <button class="sx-btn sx-btn-ghost sx-btn-icon sx-btn-sm" data-gasto="${g.id}" title="Eliminar gasto"><i class="ph ph-x"></i></button>`}
              </td>
            </tr>`).join("");
          // Solicitudes de cantidad (solo Nitrofert): lo pedido, lo entregado y su estado, encima de los gastos.
          const solicitudesHtml = (it.solicitudes ?? []).map((s) => `
            <tr class="pr-solicitud-row" data-de-item="${it.itemId}">
              <td colspan="${nCols}">↳ <i class="ph ph-clipboard-text"></i> Solicitud ${fmtFechaProy(s.fecha)} · ${formatKg(s.cantidad)} ${this._esc(it.cantUnidad)} · entregado ${formatKg(s.entregado)}
                <span class="pr-sol-estado ${this._esc(s.estado)}">${ESTADO_SOLICITUD_LABEL[s.estado] ?? this._esc(s.estado)}</span>${s.solicitadoPor ? ` · ${this._esc(s.solicitadoPor)}` : ""}${s.observaciones ? ` · <em>${this._esc(s.observaciones)}</em>` : ""}</td>
            </tr>`).join("");
          const cantCell = soloLectura
            ? `<span class="pr-num">${it.cantidad}</span>`
            : `<input type="number" class="sx-input sx-input--numeric pr-item-cant" min="0" step="0.1" value="${it.cantidad}" data-item-id="${it.itemId}" title="Cantidad por Ha">`;
          // El técnico marca el avance REAL de la labor; es dato propio, no el % de gasto. Lo marca también
          // en su portal: reportar lo que pasa en campo es su trabajo, así que no depende de soloLectura. Solo
          // se bloquea en los ítems apagados, que el agricultor no llega a ver. Se guarda con `ActualizarItem`.
        const avanceCell = !it.activo
          ? `<span class="pr-avance-pill ${this._esc(it.avanceEstado)}">${AVANCE_LABEL[it.avanceEstado] ?? it.avanceEstado}</span>`
          : `<select class="pr-fsel pr-avance-sel" data-avance="${it.itemId}">
              ${Object.entries(AVANCE_LABEL).map(([v, l]) => `<option value="${v}" ${it.avanceEstado === v ? "selected" : ""}>${l}</option>`).join("")}
            </select>`;
        const switchCell = soloLectura ? "" : `<td><label class="pr-switch"><input type="checkbox" data-item-toggle="${it.itemId}" ${it.activo ? "checked" : ""}><span class="pr-switch-track"></span></label></td>`;
          // El texto buscable se calcula una vez acá y viaja en el DOM: filtrar es recorrer filas y
          // esconderlas, nunca volver a dibujar la tabla -si se redibuja, el input pierde el foco en
          // cada tecla-.
          const buscable = this._normalizarBusqueda(`${it.nombre} ${it.categoria ?? ""} ${it.sub ?? ""}`);
          rows.push(`<tr class="pr-budget-item${it.activo ? "" : " pr-budget-item-off"}" data-item-key="${it.itemId}" data-tipo-key="${tipo}" data-sub-key="${this._esc(sub)}" data-buscar="${this._esc(buscable)}">
            <td><span class="pr-cat-dot" style="background:${categoriaColor(it.categoria)}" title="${this._esc(it.categoria || "")}"></span>${it.nitro ? `<span class="pr-dot" style="background:var(--sx-info);display:inline-block;margin-right:5px"></span>` : ""}${this._esc(it.nombre)} <span class="pr-pill na" style="font-size:10px">${this._esc(it.categoria || "")}</span></td>
            <td>${cantCell}</td>
            <td class="pr-num">${it.precioUnitario != null ? fmtCentavos(it.presupuestado) : "<span style='color:var(--sx-text-tertiary)'>sin precio</span>"}</td>
            <td class="pr-num" style="color:var(--sx-success)">${fmtCentavos(it.ejecutado)}</td>
            <td class="pr-num" style="color:${desvCls}">${it.desviacion >= 0 ? "+" : ""}${fmtPct(it.desviacion)}</td>
            <td class="pr-num">${av}%</td>
            <td>${avanceCell}</td>
          ${switchCell}
            <td>${this._esNitrofert
              ? `<button class="sx-btn sx-btn-ghost sx-btn-sm" data-solicitar-item="${it.itemId}" data-nombre="${this._esc(it.nombre)}" data-unidad="${this._esc(it.cantUnidad)}"><i class="ph ph-plus"></i> Solicitar</button>`
              : `<button class="sx-btn sx-btn-ghost sx-btn-sm" data-gasto-item="${it.itemId}" data-nombre="${this._esc(it.nombre)}"><i class="ph ph-plus"></i> Gasto</button>`}</td>
          </tr>${solicitudesHtml}${gastosHtml}`);
        });
      }
      rows.push(`<tr class="pr-budget-subtotal" data-tipo-key="${tipo}">
        <td colspan="2">Subtotal ${tipo}</td>
        <td class="pr-num">${fmtCentavos(tipoPresupuestado)}</td>
        <td class="pr-num">${fmtCentavos(tipoEjecutado)}</td>
        <td colspan="${emptyTailCols}"></td>
      </tr>`);
      grandPresupuestado += tipoPresupuestado;
      grandEjecutado += tipoEjecutado;
    }

    rows.push(`<tr class="pr-budget-grand">
      <td colspan="2">Total estimado del proyecto</td>
      <td class="pr-num">${fmtCentavos(grandPresupuestado)}</td>
      <td class="pr-num">${fmtCentavos(grandEjecutado)}</td>
      <td colspan="${emptyTailCols}"></td>
    </tr>`);

    cont.innerHTML = `
      <div class="pr-budget-toolbar">
        <span class="pr-budget-toolbar-t">Ítems del proyecto</span>
        <input id="prd-item-search" class="sx-input pr-budget-search" type="search"
               placeholder="Buscar ítem, categoría o subsección…" value="${this._esc(this._itemBusqueda)}">
        <span id="prd-item-count" class="pr-flbl"></span>
        <div class="pr-budget-toolbar-acciones">
          ${ocultos === 0 ? "" : `<button class="sx-btn sx-btn-ghost sx-btn-sm" id="prd-toggle-inactivos">
            <i class="ph ph-eye${this._mostrarInactivos ? "-slash" : ""}"></i>
            ${this._mostrarInactivos ? "Ocultar" : "Mostrar"} ${ocultos} inactivo${ocultos === 1 ? "" : "s"}
          </button>`}
          <span class="pr-budget-total-pill">Total estimado: ${fmtCentavos(grandPresupuestado)}</span>
        </div>
      </div>
      <div class="pr-tbl-wrap">
        <table class="pr-tbl pr-budget-tbl">
          <thead><tr>
            <th>Ítem</th><th>Cant.</th><th>Presupuestado</th><th>Ejecutado</th><th>Desviación</th><th>Ejec. %</th><th>Avance</th>${soloLectura ? "" : "<th>Activo</th>"}<th>Acción</th>
          </tr></thead>
          <tbody>${rows.join("")}</tbody>
        </table>
      </div>`;

    const buscador = cont.querySelector<HTMLInputElement>("#prd-item-search");
    if (buscador) {
      buscador.addEventListener("input", () => {
        this._itemBusqueda = buscador.value;
        this._filtrarItems();
      });
    }
    // Tras un guardado inline la tabla se vuelve a dibujar: hay que re-aplicar el filtro vigente.
    this._filtrarItems();

    const toggle = document.getElementById("prd-toggle-inactivos");
    if (toggle) {
      toggle.onclick = () => {
        this._mostrarInactivos = !this._mostrarInactivos;
        this._renderItems(this._detalle?.items ?? []);
      };
    }

    cont.querySelectorAll<HTMLButtonElement>("button[data-gasto-item]").forEach((btn) => {
      btn.onclick = () => this._onGasto(btn.dataset.gastoItem!, btn.dataset.nombre ?? "");
    });
    cont.querySelectorAll<HTMLButtonElement>("button[data-solicitar-item]").forEach((btn) => {
      btn.onclick = () => this._onSolicitar(btn.dataset.solicitarItem!, btn.dataset.nombre ?? "", btn.dataset.unidad ?? "");
    });
    cont.querySelectorAll<HTMLButtonElement>("button[data-gasto]").forEach((btn) => {
      btn.onclick = () => void this._onDeleteGasto(btn.dataset.gasto!);
    });
    cont.querySelectorAll<HTMLButtonElement>("button[data-gasto-edit]").forEach((btn) => {
      btn.onclick = () => this._onEditGasto(btn.dataset.gastoEdit!, btn.dataset.itemId!, btn.dataset.nombre ?? "");
    });
    cont.querySelectorAll<HTMLInputElement>(".pr-item-cant").forEach((inp) => {
      inp.addEventListener("change", () => void this._onInlineSave(inp.dataset.itemId!, { cantidad: Number(inp.value) || 0 }));
    });
    // El avance lo guardan igual el admin y el técnico (`ActualizarItem`): el Server deja al técnico tocar solo eso.
    cont.querySelectorAll<HTMLSelectElement>("select[data-avance]").forEach((sel) => {
      sel.addEventListener("change", () => void this._onInlineSave(sel.dataset.avance!, { avanceEstado: sel.value }));
    });
    cont.querySelectorAll<HTMLInputElement>("input[data-item-toggle]").forEach((inp) => {
      inp.addEventListener("change", () => void this._onInlineSave(inp.dataset.itemToggle!, { activo: inp.checked }));
    });
  }

  /**
   * Edición en línea de cantidad/activo/avance desde la tabla de ítems (sin pasar por el modal
   * "Editar paquete"). Manda SOLO el ítem tocado y SOLO el campo que cambió (`ActualizarItem`). La usan el
   * admin y el técnico; al técnico el Server le deja cambiar solo el avance.
   *
   * Antes reenviaba el paquete completo, porque el único RPC disponible reemplazaba la lista
   * entera. Con 60+ ítems eso era lento y, peor, destructivo: dos personas con el mismo proyecto
   * abierto se pisaban en silencio, porque la última en guardar escribía su copia completa encima
   * de los cambios ajenos. El RPC por ítem elimina esa clase de error.
   */
  private async _onInlineSave(
    itemId: string,
    cambio: { cantidad?: number; activo?: boolean; avanceEstado?: string },
  ): Promise<void> {
    if (!this._detalle) return;
    try {
      await ProyectoService.instance.patchItem(this._detalle.proyecto.id, itemId, cambio);
      await this._reloadDetalle();
    } catch (error) {
      errorMessageComponent.addError("Error al guardar el cambio", (error as Error).message);
      await this._reloadDetalle();
    }
  }

  /** Solo Nitrofert: pide una cantidad del ítem. `itemId` = id del CatalogoItem, como en los gastos. */
  private _onSolicitar(itemId: string, nombre: string, unidad: string): void {
    if (!this._detalle) return;
    new SolicitudModal(this._detalle.proyecto.id, itemId, nombre, unidad, () => {
      errorMessageComponent.addSuccess("Solicitud enviada", `Nitrofert la verá en el proyecto y registrará las entregas.`);
      void this._reloadDetalle();
    }).init();
  }

  /** Minúsculas y sin acentos, para que "fertilizacion" encuentre "Fertilización". */
  private _normalizarBusqueda(texto: string): string {
    return texto.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
  }

  /**
   * Filtra la tabla de presupuesto escondiendo filas, sin volver a dibujarla: así el input no pierde
   * el foco mientras se escribe, y -lo importante- los subtotales y el total general no se recalculan
   * nunca a partir de lo que se ve. Mientras hay búsqueda activa esas filas de suma se esconden, porque
   * un subtotal junto a una lista filtrada se leería como el total de lo filtrado, que no es.
   */
  private _filtrarItems(): void {
    const cont = document.getElementById("prd-items");
    if (!cont) return;

    const q = this._normalizarBusqueda(this._itemBusqueda);
    const filtrando = q.length > 0;
    const tiposConMatch = new Set<string>();
    const subsConMatch = new Set<string>();
    let visibles = 0;
    let total = 0;

    cont.querySelectorAll<HTMLTableRowElement>("tr.pr-budget-item").forEach((tr) => {
      total++;
      const coincide = !filtrando || (tr.dataset.buscar ?? "").includes(q);
      tr.style.display = coincide ? "" : "none";
      // Los gastos y, en Nitrofert, las solicitudes del ítem se esconden con él.
      cont.querySelectorAll<HTMLTableRowElement>(
        `tr.pr-gasto-row[data-de-item="${tr.dataset.itemKey}"], tr.pr-solicitud-row[data-de-item="${tr.dataset.itemKey}"]`)
        .forEach((g) => { g.style.display = coincide ? "" : "none"; });
      if (!coincide) return;
      visibles++;
      if (tr.dataset.tipoKey) tiposConMatch.add(tr.dataset.tipoKey);
      if (tr.dataset.subKey) subsConMatch.add(tr.dataset.subKey);
    });

    cont.querySelectorAll<HTMLTableRowElement>("tr.pr-budget-phase").forEach((tr) => {
      tr.style.display = !filtrando || tiposConMatch.has(tr.dataset.tipoKey ?? "") ? "" : "none";
    });
    cont.querySelectorAll<HTMLTableRowElement>("tr.pr-budget-sub").forEach((tr) => {
      tr.style.display = !filtrando || subsConMatch.has(tr.dataset.subKey ?? "") ? "" : "none";
    });
    cont.querySelectorAll<HTMLTableRowElement>("tr.pr-budget-subtotal, tr.pr-budget-grand").forEach((tr) => {
      tr.style.display = filtrando ? "none" : "";
    });

    const contador = cont.querySelector<HTMLElement>("#prd-item-count");
    if (contador) contador.textContent = filtrando ? `${visibles} de ${total}` : "";
  }

  /** `itemId` = id del CatalogoItem (el de la ruta de gastos), no el del ProyectoItem. */
  private _onGasto(itemId: string, nombre: string, gasto?: GastoResponse): void {
    if (!this._detalle) return;
    new GastoModal(this._detalle.proyecto.id, itemId, nombre, () => void this._reloadDetalle(), gasto).init();
  }

  private _onEditGasto(gastoId: string, itemId: string, nombre: string): void {
    // El gasto se busca en el detalle ya cargado en vez de serializarlo en data-*:
    // proveedor y observaciones son texto libre y no sobreviven bien a los atributos.
    const gasto = this._detalle?.items
      .flatMap((it) => it.gastos ?? [])
      .find((g) => g.id === gastoId);
    if (!gasto) return;
    this._onGasto(itemId, nombre, gasto);
  }

  private async _onDeleteGasto(gastoId: string): Promise<void> {
    try {
      await GastoService.instance.delete(gastoId);
      await this._reloadDetalle();
    } catch (error) {
      errorMessageComponent.addError("Error al eliminar el gasto", (error as Error).message);
    }
  }

  // ── PDF ──────────────────────────────────────────────────────────────────
  private _exportPdf(): void {
    const d = this._detalle;
    if (!d) return;
    const p = d.proyecto;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    let y = 18;
    doc.setFontSize(15); doc.setTextColor(15, 110, 86);
    doc.text("Proyecto agrícola — DATA AG", 14, y); y += 8;
    doc.setFontSize(12); doc.setTextColor(30);
    doc.text(p.nombre, 14, y); y += 7;
    doc.setFontSize(9); doc.setTextColor(90);
    const meta = [
      `Cliente: ${p.clienteNombre}`,
      `Cultivo: ${p.cultivoNombre}${p.variedad ? " " + p.variedad : ""} · ${formatKg(p.ha)} Ha`,
      `${p.municipio ?? ""} · Técnico: ${p.tecnicoNombre}`,
      `Snapshot ${p.snapshotAño} · IPC ${fmtPct(p.snapshotIpc)} · Inicio ${p.fechaInicio ? fmtFechaProy(p.fechaInicio) : "—"}`,
    ];
    meta.forEach((m) => { doc.text(m, 14, y); y += 5; });
    y += 3;

    doc.setFontSize(10); doc.setTextColor(30);
    doc.text("Presupuesto", 14, y); y += 6;
    doc.setFontSize(9); doc.setTextColor(60);
    const k = d.kpis;
    [
      `Presupuesto total: ${fmtCentavos(k.presupuestoTotal)}`,
      `Ejecutado: ${fmtCentavos(k.ejecutadoTotal)}   Disponible: ${fmtCentavos(k.disponible)}`,
      `Desviación global: ${(k.desviacionGlobal >= 0 ? "+" : "") + fmtPct(k.desviacionGlobal)} · Semáforo: ${k.semaforo}`,
    ].forEach((m) => { doc.text(m, 14, y); y += 5; });
    if (this._presupuesto) {
      y += 1;
      doc.text(`CAPEX: ${fmtCentavos(this._presupuesto.capex)}   OPEX proyectado: ${fmtCentavos(this._presupuesto.opexProyectado)}   Total: ${fmtCentavos(this._presupuesto.total)}`, 14, y); y += 6;

      // Desglose año a año, con el mismo criterio que el panel: con un solo año no se imprime,
      // porque la fila diría exactamente lo mismo que la línea de arriba.
      const años = this._presupuesto.detalleAnual ?? [];
      if (años.length >= 2) {
        doc.setFontSize(10); doc.setTextColor(30);
        doc.text("Desembolso año a año", 14, y); y += 6;
        doc.setFontSize(8); doc.setTextColor(60);
        doc.text("Año", 14, y); doc.text("CAPEX", 60, y); doc.text("OPEX", 110, y); doc.text("Total", 160, y); y += 4;
        doc.setDrawColor(210); doc.line(14, y, 196, y); y += 4;
        for (const a of años) {
          if (y > 280) { doc.addPage(); y = 18; }
          doc.setTextColor(40);
          doc.text(`Año ${a.anio}${a.fraccion < 1 ? ` (${Math.round(a.fraccion * 12)} meses)` : ""}`, 14, y);
          doc.text(a.capex > 0 ? fmtCentavos(a.capex) : "—", 60, y);
          doc.text(fmtCentavos(a.opex), 110, y);
          doc.text(fmtCentavos(a.total), 160, y);
          y += 5;
        }
        y += 2;
      }
    }

    doc.setFontSize(10); doc.setTextColor(30);
    doc.text("Ítems", 14, y); y += 6;
    doc.setFontSize(8); doc.setTextColor(60);
    doc.text("Ítem", 14, y); doc.text("Presup.", 120, y); doc.text("Ejecutado", 150, y); doc.text("Desv.", 185, y); y += 4;
    doc.setDrawColor(210); doc.line(14, y, 196, y); y += 4;
    for (const it of d.items ?? []) {
      if (y > 280) { doc.addPage(); y = 18; }
      doc.setTextColor(40);
      doc.text(this._trunc(it.nombre, 55), 14, y);
      doc.text(it.precioUnitario != null ? fmtCentavos(it.presupuestado) : "sin precio", 120, y);
      doc.text(fmtCentavos(it.ejecutado), 150, y);
      doc.text((it.desviacion >= 0 ? "+" : "") + fmtPct(it.desviacion), 185, y);
      y += 5;
    }
    doc.save(`Proyecto_${p.nombre.replace(/[^a-zA-Z0-9]+/g, "_")}.pdf`);
  }

  private _trunc(s: string, n: number): string { return s.length > n ? s.slice(0, n - 1) + "…" : s; }

  private _kpi(value: string, label: string, color: string, sub: string): string {
    return `<div class="pr-kcard"><div class="pr-k-val ${color}">${this._esc(value)}</div><div class="pr-k-lbl">${label}</div>${sub ? `<div class="pr-k-sub">${this._esc(sub)}</div>` : ""}</div>`;
  }

  // ── Acciones ───────────────────────────────────────────────────────────────
  private async _onEstado(estado: string): Promise<void> {
    if (!this._detalle) return;
    try {
      const saved = await ProyectoService.instance.cambiarEstado(this._detalle.proyecto.id, { estado });
      this._all = this._all.map((x) => (x.id === saved.id ? saved : x));
      this._detalle.proyecto = saved;
      // El cambio de estado deja rastro solo en el backend — se refresca la bitácora para verlo.
      this._actividades = await ActividadService.instance.getByProyectoId(saved.id).catch(() => this._actividades);
      this._renderDetalle();
      errorMessageComponent.addSuccess("Estado actualizado", `"${saved.nombre}" ahora está en ${ESTADO_LABELS[saved.estado] ?? saved.estado}.`);
    } catch (error) {
      errorMessageComponent.addError("Error al cambiar el estado", (error as Error).message);
    }
  }

  private _onDelete(p: ProyectoResponse): void {
    new ConfirmDialogComponent({
      title: "Eliminar proyecto",
      message: `¿Eliminar el proyecto "${p.nombre}"?`,
      confirmLabel: "Eliminar",
      onConfirm: async () => {
        try {
          await new ProyectoDeleteService().delete(p.id);
          this._all = this._all.filter((x) => x.id !== p.id);
          this._backToLista();
        } catch (error) {
          errorMessageComponent.addError("Error al eliminar", (error as Error).message);
        }
      },
    }).init();
  }

  /** Iniciales para el avatar del técnico ("Carlos Herrera" → "CH"). */
  private _iniciales(nombre: string): string {
    const partes = (nombre ?? "").trim().split(/\s+/).filter(Boolean);
    if (partes.length === 0) return "—";
    return this._esc((partes[0][0] + (partes[1]?.[0] ?? "")).toUpperCase());
  }

  private _esc(s: string): string {
    return (s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  }
}
